'use strict';

/**
 * Pauseable timer that will tick on exponentially growing intervals.
 */
export class VisitTimer
{
    /**
     * @param {number} base In milliseconds. Default: 1000.
     * @param {number} factor Must be greater or equal to 1. Default: 2.
     * @param {number} maximum Maximum cycles. Default: 9.
     */
    constructor(base, factor, maximum)
    {
        this.base = base || 1000;
        this.factor = factor || 2;
        this.maximum = maximum || 9;
        // Binding this method here so we don't have to create a new one for each cycle.
        this._completeCycle = this._completeCycle.bind(this);
        this.clear();
    }

    /**
     * Resets state.
     */
    clear()
    {
        if (this.timer !== null) {
            clearTimeout(this.timer);
        }
        /**
         * Identifier for the timer of the current cycle.
         * @type {number}
         */
        this.timer = null;
        /**
         * When the last timer for the current cycle was started.
         * @type {number}
         */
        this.timerStarted = 0;
        /**
         * The timeout for the timer of the current cycle.
         * @type {number}
         */
        this.timeout = 0;
        /**
         * Tracks how long the timer has been executing in total.
         * @type {number}
         */
        this.runTime = 0;
        /**
         * Tracks the current cycle, used as the exponential for the factor.
         * @type {number}
         */
        this.cycle = 0;
        /**
         * Holds the callbacks to be executed on each tick (i.e. after each cycle).
         * @type {Set<function(number)>}
         */
        this.callbacks = new Set();
    }

    /**
     * Register a callback to be executed on each tick.
     *
     * @param {function(number)} callback
     */
    addCallback(callback)
    {
        this.callbacks.add(callback);
    }

    /**
     * Remove a previously registered callback.
     *
     * @param {function(number)} callback
     */
    removeCallback(callback)
    {
        this.callbacks.delete(callback);
    }

    /**
     * Starts next cycle.
     */
    next()
    {
        if (this.cycle === this.maximum) {
            return;
        }
        this.timeout = this.base*(Math.pow(this.factor, this.cycle));
        this.cycle++;
        this.continue();
    }

    /**
     * Creates the actual timer for the current cycle, if none is running.
     *
     * Cannot be called before next().
     */
    continue()
    {
        if (this.cycle === 0 || this.timer !== null) {
            return;
        }
        this.timerStarted = performance.now();
        this.timer = setTimeout(this._completeCycle, this.timeout);
    }

    /**
     * Pause the current timer, if any.
     */
    pause()
    {
        if (this.timer === null) {
            return;
        }
        // When we pause, we need to reduce from the timeout how long we were running
        // the timer until the pause.
        const runTime = Math.floor(performance.now() - this.timerStarted);
        this.runTime+= runTime;
        this.timeout-= runTime;
        clearTimeout(this.timer);
        this.timer = null;
    }

    /**
     * Halts the current cycle and resets the timer.
     *
     * @returns {number} The total runtime of the timer.
     */
    halt() {
        this.pause();
        const runTime = this.runTime;
        this.clear();
        return runTime;
    }

    /**
     * Called by the timeout of the cycle. Executes callbacks and starts the next cycle.
     */
    _completeCycle()
    {
        this.timer = null;
        this.runTime+= this.timeout;
        for (const callback of this.callbacks.values()) {
            callback(this.runTime);
        }
        this.next();
    }
}
